// client/src/services/settingsService.ts
import { apiClient } from './api';

export interface SystemSettings {
  siteName: string;
  siteUrl: string;
  contactEmail: string;
  supportEmail: string;
  maintenanceMode: boolean;
  allowRegistration: boolean;
  maxTenantsPerRegion: number;
  defaultTenantLimits: {
    maxVehicles: number;
    maxDrivers: number;
    maxMissionsPerMonth: number;
  };
}

export interface SecuritySettings {
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireNumbers: boolean;
    requireSymbols: boolean;
    expireDays: number;
  };
  sessionTimeout: number;
  maxLoginAttempts: number;
  twoFactorRequired: boolean;
  ipWhitelist: string[];
}

export interface EmailSettings {
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassword: string;
  smtpSecure: boolean;
  fromEmail: string;
  fromName: string;
  templatesEnabled: boolean;
}

export interface SystemSetting {
  value: any;
  description: string;
  dataType: 'string' | 'number' | 'boolean' | 'object' | 'array';
  isPublic: boolean;
  requiresRestart?: boolean;
  updatedAt: string;
}

export interface SettingsResponse {
  settings: Record<string, Record<string, SystemSetting>>;
  total: number;
}

export class SettingsService {
  private readonly baseUrl = '/super-admin/settings';

  /**
   * Get all system settings or filtered by category
   */
  async getSettings(category?: string): Promise<SettingsResponse> {
    const params = category ? { category } : {};
    return apiClient.get<SettingsResponse>(this.baseUrl, { params });
  }

  /**
   * Update multiple system settings at once
   */
  async updateSettings(settings: Array<{ key: string; value: any; description?: string }>): Promise<void> {
    const promises = settings.map(({ key, value, description }) => {
      // Ensure value is never undefined - use empty array for arrays, empty string for strings, etc.
      const safeValue = value !== undefined ? value : (key.includes('whitelist') || key.includes('list') ? [] : '');
      return this.updateSetting(key, safeValue, description);
    });
    
    await Promise.all(promises);
  }

  /**
   * Update a single setting (tries PATCH first, then POST if setting doesn't exist)
   */
  async updateSetting(key: string, value: any, description?: string): Promise<SystemSetting> {
    try {
      // Try to update first
      return await apiClient.patch<SystemSetting>(`${this.baseUrl}/${key}`, { value, description });
    } catch (error: any) {
      // If setting doesn't exist (404), create it
      if (error.response?.status === 404) {
        return await this.createSetting({
          setting_key: key,
          setting_value: value,
          category: this.getCategoryForKey(key),
          description: description || this.getDescriptionForKey(key),
          data_type: this.getDataTypeForValue(value),
          is_public: this.isPublicSetting(key),
          requires_restart: false
        });
      }
      throw error;
    }
  }

  /**
   * Create a new system setting
   */
  async createSetting(setting: {
    setting_key: string;
    setting_value: any;
    category: string;
    description: string;
    data_type: 'string' | 'number' | 'boolean' | 'object' | 'array';
    is_public: boolean;
    requires_restart?: boolean;
  }): Promise<SystemSetting> {
    return apiClient.post<SystemSetting>(this.baseUrl, setting);
  }

  /**
   * Get appropriate category for a setting key
   */
  private getCategoryForKey(key: string): string {
    if (key.startsWith('password_') || key.includes('login') || key.includes('2fa') || key.includes('session')) {
      return 'security';
    }
    if (key.startsWith('smtp_') || key.includes('email')) {
      return 'email';
    }
    if (key.includes('api_') || key.includes('rate_limit')) {
      return 'api';
    }
    if (key.includes('storage') || key.includes('cache')) {
      return 'storage';
    }
    if (key.includes('performance') || key.includes('compression')) {
      return 'performance';
    }
    return 'general';
  }

  /**
   * Get appropriate description for a setting key
   */
  private getDescriptionForKey(key: string): string {
    const descriptions: Record<string, string> = {
      'site_name': 'Nom du site',
      'site_url': 'URL du site',
      'contact_email': 'Email de contact',
      'support_email': 'Email de support',
      'allow_registration': 'Autoriser les inscriptions',
      'max_tenants_per_region': 'Nombre maximum de tenants par région',
      'default_max_vehicles': 'Nombre maximum de véhicules par défaut',
      'default_max_drivers': 'Nombre maximum de chauffeurs par défaut',
      'default_max_missions_per_month': 'Nombre maximum de missions par mois par défaut',
      'password_require_uppercase': 'Exiger des majuscules dans le mot de passe',
      'password_require_numbers': 'Exiger des chiffres dans le mot de passe',
      'password_require_symbols': 'Exiger des symboles dans le mot de passe',
      'password_expire_days': 'Expiration du mot de passe en jours',
      'ip_whitelist': 'Liste blanche des adresses IP',
      'smtp_host': 'Serveur SMTP',
      'smtp_port': 'Port SMTP',
      'smtp_user': 'Utilisateur SMTP',
      'smtp_password': 'Mot de passe SMTP',
      'smtp_secure': 'Connexion SMTP sécurisée',
      'email_from_address': 'Adresse email expéditeur',
      'email_templates_enabled': 'Templates email activés'
    };
    return descriptions[key] || `Paramètre: ${key}`;
  }

  /**
   * Determine if a setting should be public
   */
  private isPublicSetting(key: string): boolean {
    const publicSettings = [
      'site_name', 'site_url', 'contact_email', 'support_email', 'allow_registration',
      'password_min_length', 'password_require_uppercase', 'password_require_numbers', 'password_require_symbols'
    ];
    return publicSettings.includes(key);
  }

  /**
   * Get data type based on value
   */
  private getDataTypeForValue(value: any): 'string' | 'number' | 'boolean' | 'object' | 'array' {
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'object' && value !== null) return 'object';
    return 'string';
  }

  /**
   * Test email configuration
   */
  async testEmailConfig(config: EmailSettings): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>(`${this.baseUrl}/test-email`, config);
  }

  /**
   * Update system settings from SystemSettings interface
   */
  async updateSystemSettings(settings: SystemSettings): Promise<void> {
    const settingsToUpdate = [
      // Use 'app_name' instead of 'site_name' to match your existing database
      { key: 'app_name', value: settings.siteName, description: 'Nom du site' },
      { key: 'site_url', value: settings.siteUrl, description: 'URL du site' },
      { key: 'contact_email', value: settings.contactEmail, description: 'Email de contact' },
      { key: 'support_email', value: settings.supportEmail, description: 'Email de support' },
      { key: 'maintenance_mode', value: settings.maintenanceMode, description: 'Mode maintenance' },
      { key: 'allow_registration', value: settings.allowRegistration, description: 'Autoriser les inscriptions' },
      { key: 'max_tenants_per_region', value: settings.maxTenantsPerRegion, description: 'Max tenants par région' },
      { key: 'default_max_vehicles', value: settings.defaultTenantLimits.maxVehicles, description: 'Véhicules max par défaut' },
      { key: 'default_max_drivers', value: settings.defaultTenantLimits.maxDrivers, description: 'Chauffeurs max par défaut' },
      { key: 'default_max_missions_per_month', value: settings.defaultTenantLimits.maxMissionsPerMonth, description: 'Missions/mois max par défaut' }
    ];

    await this.updateSettings(settingsToUpdate);
  }

  /**
   * Update security settings from SecuritySettings interface
   */
  async updateSecuritySettings(settings: SecuritySettings): Promise<void> {
    const settingsToUpdate = [
      { key: 'password_min_length', value: settings.passwordPolicy.minLength, description: 'Longueur minimale du mot de passe' },
      { key: 'password_require_uppercase', value: settings.passwordPolicy.requireUppercase, description: 'Majuscules requises' },
      { key: 'password_require_numbers', value: settings.passwordPolicy.requireNumbers, description: 'Chiffres requis' },
      { key: 'password_require_symbols', value: settings.passwordPolicy.requireSymbols, description: 'Symboles requis' },
      { key: 'password_expire_days', value: settings.passwordPolicy.expireDays, description: 'Expiration mot de passe (jours)' },
      { key: 'session_timeout_minutes', value: settings.sessionTimeout, description: 'Timeout de session (minutes)' },
      { key: 'max_login_attempts', value: settings.maxLoginAttempts, description: 'Tentatives de connexion max' },
      { key: 'require_2fa', value: settings.twoFactorRequired, description: '2FA obligatoire' },
      // Removed ip_whitelist since there's no form field for it
    ];

    await this.updateSettings(settingsToUpdate);
  }

  /**
   * Update email settings from EmailSettings interface
   */
  async updateEmailSettings(settings: EmailSettings): Promise<void> {
    const settingsToUpdate = [
      { key: 'smtp_host', value: settings.smtpHost, description: 'Serveur SMTP' },
      { key: 'smtp_port', value: settings.smtpPort, description: 'Port SMTP' },
      { key: 'smtp_user', value: settings.smtpUser, description: 'Utilisateur SMTP' },
      { key: 'smtp_password', value: settings.smtpPassword, description: 'Mot de passe SMTP' },
      { key: 'smtp_secure', value: settings.smtpSecure, description: 'Connexion sécurisée SMTP' },
      { key: 'email_from_address', value: settings.fromEmail, description: 'Email expéditeur' },
      { key: 'smtp_from_name', value: settings.fromName, description: 'Nom expéditeur' },
      { key: 'email_templates_enabled', value: settings.templatesEnabled, description: 'Templates email activés' }
    ];

    await this.updateSettings(settingsToUpdate);
  }
}

// Export singleton instance
export const settingsService = new SettingsService();